package org.teddapp.project.sync

import com.tedd.db.TeddDatabase
import kotlinx.coroutines.CoroutineScope
import org.teddapp.project.core.CoreSession
import org.teddapp.project.core.SupabaseJournalTransport
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.db.nowMillis
import org.teddapp.project.ui.AppSettings
import org.teddapp.project.ui.settings.SyncBindings

/**
 * Factory multiplatform : crée [SyncBindings] depuis les credentials Supabase.
 *
 * Appelée depuis chaque entrée de l'app :
 *  - Desktop : `main.kt` lit les env vars `SUPABASE_URL` / `SUPABASE_ANON_KEY`.
 *  - Android : `MainActivity.onCreate` lit les mêmes env vars (ou un BuildConfig).
 *  - iOS : `MainViewController()` reçoit les credentials depuis Swift (Info.plist
 *    ou variable d'environnement injectée).
 *
 * Retourne null si :
 *  - les credentials sont manquants (sync désactivée par défaut sans config) ;
 *  - la création du client échoue (lib manquante, format URL invalide, etc.).
 */
fun createSyncBindings(
    db: TeddDatabase,
    appSettings: AppSettings,
    scope: CoroutineScope,
    supabaseUrl: String?,
    supabaseAnonKey: String?,
    coreSession: CoreSession? = null,
): SyncBindings? {
    val url = supabaseUrl?.takeIf { it.isNotBlank() } ?: return null
    val key = supabaseAnonKey?.takeIf { it.isNotBlank() } ?: return null
    return runCatching {
        val client = SupabaseRemoteStore.createClient(url, key)
        val remote = SupabaseRemoteStore(client)
        val clock = Clock { nowMillis() }
        val engine = SqlDelightSyncEngine(db, remote, clock)
        // Le journal du noyau ne passe pas par le moteur LWW : le noyau possède son
        // cycle, et l'application ne lui prête que le transport.
        coreSession?.transport = SupabaseJournalTransport(client, userId = TeddIdentity.USER_ID)
        val manager = SyncManager(db, engine, appSettings, scope, journalCycle = {
            coreSession?.syncNow()
        })
        // Extrait l'hôte de l'URL pour un affichage compact (ex. "tedd-prod.supabase.co")
        val displayUrl = url.removePrefix("https://").removePrefix("http://").substringBefore("/")
        // Le guetteur écoute les changements distants au lieu d'attendre le battement.
        val watcher = LiveWatcher(client, scope) { manager.liveCycle() }
        if (appSettings.syncEnabled) watcher.start()
        object : SyncBindings {
            override val state = manager.state
            override val projectUrl: String = displayUrl
            override fun onToggle(on: Boolean) {
                manager.setEnabled(on)
                if (on) watcher.start() else watcher.stop()
            }
            override fun onSyncNow() { manager.syncNow() }
            override fun onCheckRemote() { manager.checkRemote() }
        }
    }.getOrNull()
}

/** @deprecated Renommée en [createSyncBindings] pour multiplatform. */
@Deprecated(
    message = "Use createSyncBindings instead (multiplatform).",
    replaceWith = ReplaceWith("createSyncBindings(db, appSettings, scope, supabaseUrl, supabaseAnonKey)"),
)
fun createDesktopSyncBindings(
    db: TeddDatabase,
    appSettings: AppSettings,
    scope: CoroutineScope,
    supabaseUrl: String?,
    supabaseAnonKey: String?,
): SyncBindings? = createSyncBindings(db, appSettings, scope, supabaseUrl, supabaseAnonKey)
